import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SeasonalTheme = {
  key: string;
  label: string;
  description: string | null;
  badge_emoji: string | null;
  badge_text: string | null;
  accent_hsl: string;
  ribbon_gradient: string;
  footer_note: string | null;
};

type Ctx = { season: SeasonalTheme | null; loading: boolean };
const SeasonalThemeContext = createContext<Ctx>({ season: null, loading: true });

export const useSeason = () => useContext(SeasonalThemeContext);

export const SeasonalThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [season, setSeason] = useState<SeasonalTheme | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("seasonal_themes")
        .select("*")
        .eq("active", true)
        .maybeSingle();
      if (cancelled) return;
      const t = data as SeasonalTheme | null;
      setSeason(t && t.key !== "default" ? t : null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (season) {
      root.style.setProperty("--season-accent", season.accent_hsl);
      root.style.setProperty("--season-ribbon", season.ribbon_gradient);
      root.style.setProperty("--season-badge-bg", `hsl(${season.accent_hsl} / 0.12)`);
      root.style.setProperty("--season-badge-fg", `hsl(${season.accent_hsl})`);
    } else {
      root.style.removeProperty("--season-accent");
      root.style.removeProperty("--season-ribbon");
      root.style.removeProperty("--season-badge-bg");
      root.style.removeProperty("--season-badge-fg");
    }
  }, [season]);

  return (
    <SeasonalThemeContext.Provider value={{ season, loading }}>
      {children}
    </SeasonalThemeContext.Provider>
  );
};

export default SeasonalThemeProvider;